<script setup lang="ts">
import GenericError from '~/components/user-interface/generic-error.vue';
import { useSiteStore } from '~/stores/site.store';
import { useServices } from '~/composables/useServices';
import Button from '../design-system/button.vue';
import {
  handleSuccessfulReg,
  handleUnSuccessfulReg,
  getAdditionalProps,
  handleProgressFields,
  sourceOfFundsError,
  getActionId,
} from '~/utilities/form-builder.reg';
import type { IFormBuilderReference } from '~/interfaces/form/formBuilder';
import InputMask from 'primevue/inputmask';
import { registerRoutes } from 'assets/data/config';
import { ChevronRightIcon } from '@heroicons/vue/24/solid';
import { ArrowPathIcon } from '@heroicons/vue/24/outline';
import type {
  IPartialRegStepOneRequest,
  IRegisterRequest,
} from '~/interfaces/dto/auth/request';
import { generateTranslationKey } from '~/utilities/data-transformers';
import type {
  IPartialRegStepOneResponse,
  IRegisterResponse,
} from '~/interfaces/dto/auth/response';
import { getUserDeviceInfo } from '~/utilities/device-info';

const defaultState = {
  username: '',
  password: '',
  passwordConfirmation: '',
  firstname: '',
  lastname: '',
  email: '',
  referralCode: '',
  idNumberType: 'idNumber',
  idNumber: '',
  sourceOfFunds: '',
  preferredLanguage: 'en-US',
  receivePromotionalInformation: false,
  terms: false,
  agreeToAll: false,
  dateOfBirth: null,
};

const model = ref(defaultState);

const form = ref<IFormBuilderReference>();

const route = useRoute();
const { $t, $enabled, $trackEvent, $dayjs } = useNuxtApp();
const site = useSiteStore();
const auth = useAuthStore();
const { $authService } = useServices();

const isPartialReg = computed(() => $enabled('myaccount.enablepartialreg'));
const errorCode = ref(null);
const errorRef = ref<string>('');
const formLoading = ref<boolean>(false);
const progressBarFields = ref();
const token = ref(null);
const tokenFormatted = ref(null);
const otpValid = ref<boolean>(false);

const now = ref<ReturnType<typeof $dayjs>>($dayjs());
const otpExpiryTime = ref<ReturnType<typeof $dayjs>>();
const accountExpiryTime = ref<ReturnType<typeof $dayjs>>();
const timer = ref<ReturnType<typeof setTimeout>>();
const interval = ref<ReturnType<typeof setInterval>>();
const expiryInterval = ref<string>('');
const isOTPExpired = ref<boolean>(false);

const partialRegStep = ref<number>(0);

const campaign = computed(() => {
  const value = route.params.campaign;
  return typeof value === 'string' && value ? value : undefined;
});

const showRegForm = computed(
  () =>
    !isPartialReg.value || (isPartialReg.value && partialRegStep.value === 0),
);

const progressBar = computed(() => {
  if (!progressBarFields.value) return 0;

  const excludeKeys = new Set([
    'idNumberType',
    'receivePromotionalInformation',
    'preferredLanguage',
    'referralCode',
    'welcomeOffer',
    'agreeToAll',
    'language',
  ]);

  const relevantFields = Object.entries(progressBarFields.value).filter(
    ([key]) => !excludeKeys.has(key),
  );

  if (relevantFields.length === 0) return 0;

  const validFields = relevantFields.filter(([name]) => {
    const el = model.value[name];
    return !!el && !form.value?.formErrors?.[name];
  }).length;

  return validFields / relevantFields.length;
});

const enableWelcomeOffer = computed(() =>
  site.getFeatureFlags.get('website.enablewelcomeoffer'),
);

const setDefaultState = () => (model.value = defaultState);

const handleOnLoad = () => {
  formLoading.value = false;
  setTimeout(() => {
    handleProgressFields(progressBarFields, form);
  }, 500);
};

const handleError = (e: string) => {
  errorCode.value = e;
  errorRef.value = e;
};

const handleSignIn = () => {
  if (registerRoutes.includes(route.path)) {
    navigateTo('/login');
  } else site.activateModal('login');
};

const verifyOTP = () => {
  handleError('');
  let otp = token.value;
  otp = otp.replace(/ +/g, '');
  if (!otp.includes('_') && otp.length > 4) {
    otpValid.value = true;
    tokenFormatted.value = otp;
  } else {
    otpValid.value = false;
  }
};

const validatePartialRegToken = async () => {
  formLoading.value = true;
  const validationResponse = await $authService.verifyPartialRegOtp({
    username: model.value['username'],
    otp: tokenFormatted.value,
  });

  if (validationResponse.isSuccessful) {
    $trackEvent('RegistrationEvent', 'PARTIAL_REG_STEP_TWO', {
      msisdn: model?.value['username'],
      ...(!!campaign.value && { campaign: campaign.value }),
    });
    await $authService.login({
      username: model.value.username,
      password: model.value.password,
    });
    await $trackEvent('RegistrationEvent', 'REG_INIT', {
      msisdn: model?.value?.username,
      ...(!!campaign.value && { campaign: campaign.value }),
    });
    clearInterval(interval.value);
    clearTimeout(timer.value);
  } else {
    errorCode.value = $t(
      generateTranslationKey(
        `jpc-reg-${validationResponse?.error?.message || 'general-error'}`,
      ),
    );
    errorRef.value = $t(
      generateTranslationKey(
        validationResponse?.error?.code?.toString() || 'general-error',
      ),
    );
  }
  formLoading.value = false;
};

const requestNewOtp = async () => {
  formLoading.value = true;
  $trackEvent('RegistrationEvent', 'PARTIAL_REG_REQUEST_NEW_OTP', {
    msisdn: model?.value['username'],
    ...(!!campaign.value && { campaign: campaign.value }),
  });
  const newOTPResponse = await $authService.requestPartialRegOtp(
    model.value['username'],
  );
  if (newOTPResponse.isSuccessful) {
    now.value = $dayjs();
    otpExpiryTime.value = $dayjs(
      new Date().getTime() + site.getPartialRegOTPTimeout,
    );
    accountExpiryTime.value = $dayjs(
      new Date().getTime() + site.getPartialRegTimeBeforeDeletion,
    );
    handleError('');
    isOTPExpired.value = false;
    setRegOTPInterval();
    setRegTimeout();
  } else {
    errorCode.value = $t(
      generateTranslationKey(
        `jpc-reg-${newOTPResponse?.error?.message || 'general-error'}`,
      ),
    );
    errorRef.value = $t(
      generateTranslationKey(
        newOTPResponse?.error?.code?.toString() || 'general-error',
      ),
    );
  }
  formLoading.value = false;
};

const updateCountdown = () => {
  now.value = $dayjs();

  if (now.value.isAfter(otpExpiryTime.value)) {
    if (now.value.isAfter(accountExpiryTime.value)) partialRegStep.value = 0;
    else partialRegStep.value = 1;

    setDefaultState();
    isOTPExpired.value = true;
    errorCode.value = $t('your-otp-has-expired');
    clearInterval(interval.value);
  }
  const remainingSeconds = otpExpiryTime.value.diff(now.value, 'seconds');
  expiryInterval.value = $dayjs
    .duration(remainingSeconds, 'seconds')
    .format('HH:mm:ss');
};

const setRegOTPInterval = () => {
  interval.value = setInterval(updateCountdown, 1000);
};

const setRegTimeout = () => {
  timer.value = setTimeout(() => {
    now.value = $dayjs();
    if (now.value.isAfter(accountExpiryTime.value)) {
      partialRegStep.value = 0;
      setDefaultState();
    }
  }, accountExpiryTime.value.diff(now.value));
};

const register = async () => {
  try {
    formLoading.value = true;
    errorCode.value = '';

    if (model.value.referralCode !== '')
      if (!/^[a-zA-Z0-9]{3,25}$/.test(model.value.referralCode)) {
        throw new Error('Invalid referralCode');
      }

    const deviceInfo = getUserDeviceInfo();
    const additionalProps = getAdditionalProps(
      deviceInfo,
      model,
      isPartialReg.value,
      campaign.value ? campaign.value : null,
    );
    const language = model.value.preferredLanguage;
    const { executeRecaptcha } = await useRecaptcha();
    const request = {
      ...additionalProps,
      ...model.value,
      ...(model.value?.password === '' && { password: null }),
      ...(form.value?.hasRecaptcha && {
        recaptcha: await executeRecaptcha('register'),
      }),
      ...(model.value.referralCode === '' && { referralCode: null }),
      ...(!!model.value?.dateOfBirth && {
        dateOfBirth: model.value?.dateOfBirth.utc(true).toISOString(),
      }),
      language,
    };
    if (!request.idNumber) delete request.idNumberType;

    delete request.preferredLanguage;
    delete request.passwordConfirmation;

    for (const key in request) {
      if (request[key] === '') delete request[key];
    }

    let registrationResponse: IPartialRegStepOneResponse | IRegisterResponse;

    if (isPartialReg.value) {
      if (partialRegStep.value === 0) {
        delete request.language;
        delete request.agreeToAll;
        delete request.terms;
        delete request.referralCode;

        await $trackEvent('RegistrationEvent', 'PARTIAL_REG_STEP_ONE', {
          msisdn: model?.value['username'],
          ...(!!campaign.value && { campaign: campaign.value }),
        });
        const partialRegResponse = await $authService.initPartialReg(
          request as unknown as IPartialRegStepOneRequest,
        );

        if (partialRegResponse.isSuccessful) {
          partialRegStep.value = 1;
          otpExpiryTime.value = $dayjs(
            new Date().getTime() + site.getPartialRegOTPTimeout,
          );
          accountExpiryTime.value = $dayjs(
            new Date().getTime() + site.getPartialRegTimeBeforeDeletion,
          );
          setRegOTPInterval();
          setRegTimeout();
        } else {
          errorCode.value = $t(
            generateTranslationKey(
              `jpc-reg-${partialRegResponse?.error?.message}`,
            ) || 'general-error',
          );
          errorRef.value = $t(
            generateTranslationKey(
              partialRegResponse?.error?.code?.toString(),
            ) || 'general-error',
          );
          $trackEvent('RegistrationEvent', 'PARTIAL_REG_FAILURE', {
            msisdn: form?.value?.model?.['username'],
            reason: partialRegResponse?.cause?.message,
            ...(!!campaign.value && { campaign: campaign.value }),
          });
        }
      }
    } else {
      $trackEvent('RegistrationEvent', 'REG_INIT', {
        msisdn: model?.value['username'],
        ...(!!campaign.value && { campaign: campaign.value }),
      });
      registrationResponse = await $authService.register(
        request as unknown as IRegisterRequest,
      );
      if (registrationResponse?.isSuccessful) {
        handleSuccessfulReg(
          getActionId(),
          registrationResponse as IRegisterResponse,
          model,
          errorCode,
        );
        $trackEvent('RegistrationEvent', 'REG_SUCCESS', {
          msisdn: model?.value['username'],
          ...(!!campaign.value && { campaign: campaign.value }),
        });
      } else {
        handleUnSuccessfulReg(
          registrationResponse as IRegisterResponse,
          form,
          errorCode,
          errorRef,
        );
        $trackEvent('RegistrationEvent', 'REG_FAILURE', {
          msisdn: form?.value?.model?.['username'],
          reason: errorRef.value || registrationResponse?.cause?.message,
          ...(!!campaign.value && { campaign: campaign.value }),
        });
      }
    }
  } catch (e) {
    if (e === 'deceased-or-minor-reg') errorCode.value = $t(e);
    else errorCode.value = $t('registration-failed');
    $trackEvent('RegistrationEvent', 'REG_FAILURE', {
      msisdn: form?.value?.model?.['username'],
      reason: errorCode.value || e?.cause?.message,
      ...(!!campaign.value && { campaign: campaign.value }),
    });
    console.error(e);
  } finally {
    formLoading.value = false;
  }
};

onBeforeMount(async () => {
  formLoading.value = true;
});
onMounted(() => {
  if (isPartialReg.value && !auth.accountModal)
    [...document.querySelectorAll('[data-p="modal"]')].forEach((el) =>
      el.classList.add('!overflow-visible'),
    );
});
onBeforeUnmount(() => {
  if (isPartialReg.value && !auth.accountModal)
    [...document.querySelectorAll('[data-p="modal"]')].forEach((el) =>
      el.classList.remove('!overflow-visible'),
    );
  if (!!timer.value) clearTimeout(timer.value);
  if (!!interval.value) clearInterval(interval.value);
});
</script>

<template>
  <div class="bg-layer-secondary">
    <UserInterfaceRegProgressBar
      v-if="partialRegStep !== 1"
      :progress="progressBar"
    />
    <div
      class="transition-all duration-300 ease-in-out"
      :class="{ 'min-h-80': partialRegStep !== 1 }"
    >
      <UserInterfaceDynamicFormBuilder
        v-if="showRegForm"
        ref="form"
        v-model="model"
        :template-name="isPartialReg ? 'partial registration' : 'registration'"
        :container-classes="[{ 'px-3 py-2': true }]"
        :should-auto-focus="false"
        @loaded="handleOnLoad"
        @error="handleError"
        @edited="handleError('')"
      >
        <template #before>
          <UserInterfaceDynamicFormBuilderPseudoElements />
        </template>
        <template #form-actions="formActionProps">
          <LazyMarketingWelcomeOfferRegistration
            v-if="enableWelcomeOffer"
            :validationPassed="
              formActionProps?.form?.validated &&
              !formActionProps?.form?.invalid
            "
          />
          <GenericError
            v-if="errorCode"
            class="mb-3 max-w-72 mx-auto"
            state="danger"
            :reference-code="errorRef"
          >
            {{ errorCode }}
          </GenericError>
          <div
            class="w-full p-4"
            :class="{
              'bg-base': !('no-action-bg' in $attrs),
            }"
          >
            <UserInterfaceModalActionBadge
              v-if="
                formActionProps?.form?.steps?.isAtFirstStep &&
                !!formActionProps?.form?.schema?.hasSteps
              "
              left-label="already-have-account"
              action-text="login"
              :icon-name="ChevronRightIcon"
              @action="handleSignIn"
            />
            <Button
              v-if="
                !formActionProps?.form?.steps?.isAtLastStep &&
                !!formActionProps?.form?.schema?.hasSteps
              "
              element-name="step-one-next-registration"
              :disabled="
                !(
                  formActionProps?.form?.steps?.availableSteps?.[0]
                    ?.validated &&
                  !formActionProps?.form?.steps?.availableSteps?.[0]?.invalid
                )
              "
              type="primary"
              class="w-full justify-center"
              @click="
                (formActionProps?.form?.steps?.next(),
                sourceOfFundsError(formActionProps.form))
              "
            >
              {{ $t('next') }}
            </Button>
            <div
              v-if="
                formActionProps?.form?.steps?.isAtLastStep &&
                !!formActionProps?.form?.schema?.hasSteps
              "
              class="last-page-controls flex gap-4 rounded-b-lg"
            >
              <Button
                v-if="!formActionProps?.form?.steps?.isAtFirstStep"
                element-name="step-two-previous-registration"
                :type="
                  'no-action-bg' in $attrs
                    ? 'tertiary'
                    : 'secondary-alternative'
                "
                class="w-full justify-center dark:!bg-dark-700"
                @click="formActionProps?.form?.steps.previous()"
              >
                {{ $t('previous') }}
              </Button>
              <Button
                element-name="jpc-register"
                group="primary"
                class="w-full justify-center"
                :disabled="
                  !(
                    formActionProps?.form?.steps?.availableSteps?.[1]
                      ?.validated &&
                    !formActionProps?.form?.steps?.availableSteps?.[1]?.invalid
                  )
                "
                @click="register"
              >
                {{ $t('jpc-register') }}
              </Button>
            </div>
            <div
              v-if="!formActionProps?.form?.schema?.hasSteps"
              class="last-page-controls flex flex-col gap-1"
            >
              <UserInterfaceModalActionBadge
                v-if="!auth.isLoggedIn"
                left-label="already-have-account"
                action-text="login"
                :icon-name="ChevronRightIcon"
                @action="handleSignIn"
              />
              <Button
                element-name="jpc-register"
                group="primary"
                class="w-full justify-center"
                :disabled="
                  !(
                    formActionProps?.form?.validated &&
                    !formActionProps?.form?.invalid
                  )
                "
                @click="register"
              >
                {{ $t('jpc-register') }}
              </Button>
            </div>
          </div>
        </template>
      </UserInterfaceDynamicFormBuilder>
      <div v-if="isPartialReg && partialRegStep === 1" class="flex flex-col">
        <div class="flex flex-col gap-2 p-4">
          <span class="text-base-priority">{{ $t('enter-otp') }}</span>
          <span v-if="!isOTPExpired" class="text-base-priority">
            {{ `${$t('your-otp-will-expire-in')}:` }}
            <strong class="text-primary-blue-100">{{ expiryInterval }}</strong>
          </span>
          <div>
            <InputMask
              v-model="token"
              mask="9   9   9   9   9"
              placeholder="_ _ _ _ _"
              slot-char="_ _ _ _ _"
              autocomplete="one-time-code"
              class="w-full otp-token text-lg cursor-pointer"
              @update:model-value="verifyOTP"
            />
          </div>
          <GenericError
            v-if="errorCode"
            class="mb-3 max-w-72 mx-auto"
            :state="isOTPExpired ? 'warn' : 'danger'"
            :reference-code="errorRef"
          >
            {{ errorCode }}
          </GenericError>
        </div>
        <div
          class="w-full p-4 rounded-b-lg"
          :class="{
            'bg-base': !('no-action-bg' in $attrs),
          }"
        >
          <UserInterfaceModalActionBadge
            left-label="no-reset-code"
            action-text="try-again"
            :icon-name="ArrowPathIcon"
            @action="requestNewOtp"
          />
          <div class="flex gap-4">
            <Button
              v-if="!isOTPExpired"
              element-name="step-one-next-registration"
              :disabled="!otpValid"
              type="primary"
              class="w-full justify-center"
              @click="validatePartialRegToken"
            >
              {{ $t('continue') }}
            </Button>
          </div>
        </div>
      </div>
    </div>
    <LazyUserInterfaceGenericLoader v-if="formLoading" container />
  </div>
</template>
<style lang="scss">
input.otp-token {
  @apply pl-5 tracking-[5px] text-lg text-center dark:placeholder:text-dark-600 placeholder:text-light-400;
}

@media (max-width: 420px) {
  input.otp-token {
    @apply tracking-[3px];
  }
}
</style>
